import React, { ComponentType, useState } from "react"
import { compose } from "@reduxjs/toolkit"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Grid, Typography, DialogContent, styled } from "@mui/material"

import { useAppDispatch } from "redux/hooks"
import { withModalSubscription } from "hocs/features"
import { closeDialog } from "redux/features/dialog/dialogSlice"
import PollingService from "redux/api/pollingService"
import { showAlert } from "redux/features/notice/noticeSlice"
import { makeCancelSubscription } from "redux/features/subscription/subscriptionThunk"
import { formatDate } from "helpers/formatters"
import Spinner from "components/ui/Spinner"
import Close from "components/ui/ModalClose"
import Button from "components/ui/Button"
import { CLIENT_SUBSCRIPTIONS_MATCH } from "config"
import { SubscriptionType } from "types"

type Props = React.HTMLAttributes<HTMLDivElement> & {
  subscription: SubscriptionType
}

const SubscriptionCancelModal = ({ subscription }: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [isSubmitting, setSubmitting] = useState(false)

  const handleClose = () => {
    if (PollingService.isPolling()) {
      return dispatch(
        showAlert({
          message: t("common.request_in_progress"),
        }),
      )
    }
    dispatch(closeDialog())
    navigate(CLIENT_SUBSCRIPTIONS_MATCH)
  }

  const handleUnsubscribe = () => {
    setSubmitting(true)

    const { id } = subscription

    if (id) {
      dispatch(makeCancelSubscription(id))
        .then(() => {
          dispatch(closeDialog())
          navigate(CLIENT_SUBSCRIPTIONS_MATCH)
        })
        .catch((error) => {
          dispatch(showAlert({ message: error }))
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  }

  return (
    <DialogContent>
      <Spinner open={isSubmitting} />
      <Close onClick={handleClose} />
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Title variant="h4">{t("features.subscription.cancel.title")}</Title>
        <Description variant="h6">
          {t("features.subscription.cancel.description", {
            end_at: formatDate(subscription.end_at),
          })}
        </Description>
        <Unsubscribe onClick={handleUnsubscribe}>
          {t("features.subscription.cancel.unsubscribe")}
        </Unsubscribe>
      </Grid>
    </DialogContent>
  )
}

export default compose<ComponentType>(withModalSubscription)(
  SubscriptionCancelModal,
)

const Title = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  marginBottom: "24px",
  [theme.breakpoints.down("lg")]: {
    fontSize: "1.375rem",
  },
}))

const Description = styled(Typography)(({ theme }) => ({
  margin: "12px 0",
  [theme.breakpoints.down("lg")]: {},
}))

const Btn = styled(Button)(({ theme }) => ({
  borderRadius: "21px",
  fontSize: "0.875rem",
  fontWeight: "500",
  textTransform: "inherit",
  height: "44px",
  padding: "0 20px",
  width: "175px",
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
    margin: "8px 0",
    width: "100%",
  },
}))

const Unsubscribe = styled(Btn)(({ theme }) => ({
  background: "rgba(172, 172, 172, 0.1)",
  color: theme.palette.common.black,
}))
